<template>
  <div
    class="bb-table-wrapper mt-3"
    id="artefacts-board"
    @scroll="scheduleScrollStateRefresh"
  >
    <vue-good-table
      :columns="headings"
      :fixed-header="false"
      :rows="artefacts"
      :select-options="{
        enabled: false,
        selectOnCheckboxOnly: false,
        selectionText: 'Artefact(s) selected',
        clearSelectionText: 'clear',
      }"
      :search-options="{
        enabled: true,
        placeholder: 'search...',
      }"
      :pagination-options="{
        enabled: true,
        mode: 'records',
        perPage: 7,
        position: 'bottom',
        dropdownAllowAll: true,
        setCurrentPage: 1,
        nextLabel: 'next',
        prevLabel: 'prev',
        rowsPerPageLabel: 'Artefacts per page',
        ofLabel: 'of',
        allLabel: 'All',
      }"
      :row-style-class="rowStyleClassFn"
      @on-page-change="scheduleScrollStateRefresh"
      @on-per-page-change="scheduleScrollStateRefresh"
      v-on:cell-click="onCellClick"
      styleClass="vgt-table bordered"
      theme="black-rhino"
    >
      <template v-slot:table-row="props">
        <counter-dialog ref="counterDialog"></counter-dialog>
        <confirm-dialog ref="confirmDialog"></confirm-dialog>

        <!-- no heading is blank label -->
        <span v-if="props.column.label == ''"> </span>

        <!-- held_image: show exclamation (no_location), or tick (held), or nothing -->
        <span v-if="props.column.field === 'held_image'">
          <img
            v-if="props.row.no_location"
            :src="exclamationImg"
            height="50"
            width="50"
            alt="no location error"
          />
          <img
            v-else-if="props.row.held"
            :src="tickImg"
            height="50"
            width="50"
            alt="held artefact"
          />
        </span>

        <!-- usable_image: tick / cross / exclamation based on usable_status, with tooltip + orange dot on No/Partial -->
        <span v-else-if="props.column.field === 'usable_image'">
          <ToolTip
            v-if="
              props.row.usable_status !== 'Yes' && props.row.unusable_reasons
            "
            tableWrapperMode
            position="top"
            :text="props.row.unusable_reasons"
          >
            <span class="bb-usable-icon-wrapper">
              <img
                v-if="props.row.usable_status === 'Yes'"
                :src="tickImg"
                height="50"
                width="50"
                alt="usable weapon"
              />
              <img
                v-else-if="props.row.usable_status === 'No'"
                :src="crossImg"
                height="50"
                width="50"
                alt="unusable weapon"
              />
              <img
                v-else-if="props.row.usable_status === 'Partial'"
                :src="exclamationImg"
                height="50"
                width="50"
                alt="partially usable weapon"
              />
              <span class="bb-orange-circle"></span>
            </span>
          </ToolTip>
          <span v-else>
            <img
              v-if="props.row.usable_status === 'Yes'"
              :src="tickImg"
              height="50"
              width="50"
              alt="usable weapon"
            />
            <img
              v-else-if="props.row.usable_status === 'No'"
              :src="crossImg"
              height="50"
              width="50"
              alt="unusable weapon"
            />
            <img
              v-else-if="props.row.usable_status === 'Partial'"
              :src="exclamationImg"
              height="50"
              width="50"
              alt="partially usable weapon"
            />
          </span>
        </span>

        <span v-else-if="props.column.field === 'attached_image'">
          <span
            v-if="props.row.attached"
            class="d-inline-flex align-artefacts-center justify-content-center"
            v-tooltip:top="getAttachedTooltipText(props.row)"
          >
            <img
              :src="tickImg"
              :class="isTickVisible(props)"
              height="50"
              width="50"
              alt="attached artefact"
            />
          </span>
          <img
            v-else
            :src="tickImg"
            :class="isTickVisible(props)"
            height="50"
            width="50"
            alt="attached artefact"
          />
        </span>

        <span v-else-if="props.column.field === 'attachment_count'">
          {{ getAttachmentHostCount(props.row) }}
        </span>

        <span v-else-if="props.column.field === 'attachment_artefacts_image'">
          <img
            v-if="props.row.attach_point"
            class="bbActionIcon"
            :src="attachedImg"
            @click.stop="manageAttached(props.row)"
            alt="attached artefacts image"
          />
        </span>

        <!-- other *_image flag fields: show tick when underlying flag true (exclude action/contents/held/usable icons) -->
        <span
          v-else-if="
            props.column.field.endsWith('_image') &&
            props.column.field !== 'adjust_image' &&
            props.column.field !== 'contents_image' &&
            props.column.field !== 'held_image' &&
            props.column.field !== 'usable_image' &&
            props.column.field !== 'attached_image' &&
            props.column.field !== 'attachment_artefacts_image'
          "
        >
          <img
            :src="tickImg"
            :class="isTickVisible(props)"
            height="50"
            width="50"
            alt="maxed image"
          />
        </span>

        <!-- row number -->
        <span v-if="props.column.field === 'rowNumber'">
          {{
            props.row.originalIndex !== undefined
              ? props.row.originalIndex + 1
              : props.index + 1
          }}
        </span>

        <!-- show/hide tally quantity based on tallyable flag -->
        <span v-else-if="props.column.field === 'tally_qty'">
          {{
            isRowTallyable(props.row)
              ? (props.formattedRow[props.column.field] ?? '')
              : ''
          }}
        </span>

        <!-- Armour-specific: only show coverage/AP/HP/layer/dex_pen when artefact is equipped -->
        <span
          v-else-if="
            props.row.artefact_type === 'Armour' &&
            ['coverage', 'ap', 'hp', 'layer', 'dex_pen'].includes(
              props.column.field,
            )
          "
        >
          {{ props.row.equipped ? props.formattedRow[props.column.field] : '' }}
        </span>

        <!-- format enc -->
        <span v-else-if="props.column.field === 'enc'">
          {{
            props.row.enc !== null && props.row.enc !== undefined
              ? parseFloat(props.row.enc).toFixed(2)
              : ''
          }}
        </span>

        <!-- artefact image: render thumbnail instead of raw data URL/text, with default fallback -->
        <span v-else-if="props.column.field === 'image'">
          <img
            :src="props.row['image'] || defaultImage"
            height="80"
            width="80"
            alt="artefact image"
          />
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>

        <!-- show/hide adjust image dep on tallyable -->
        <span
          v-if="
            props.column.field === 'adjust_image' && isRowTallyable(props.row)
          "
        >
          <img
            class="bbActionIcon"
            :src="adjustImg"
            @click.stop="adjustQuantity(props.row)"
            alt="adjust image"
          />
        </span>

        <!-- show/hide contents image dep on storage and content cnt > 0 -->
        <span
          v-if="
            props.column.field === 'contents_image' &&
            props.row.storage &&
            props.row.contents_count_direct > 0
          "
        >
          <img
            class="bbActionIcon"
            :src="contentsImg"
            @click.stop="manageContents(props.row)"
            alt="contents image"
          />
        </span>
      </template>

      <template #emptystate>*** No artefacts for campaign ***</template>
    </vue-good-table>
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'

import { VueGoodTable } from 'vue-good-table-next'
import CounterDialog from './CounterDialog.vue'
import ConfirmDialog from './ConfirmDialog.vue'
import ToolTip from '../components/ToolTip.vue'
import { inject } from 'vue'

import exclamationImg from '../assets/images/bbExclamation2.png'
import adjustImg from '../assets/images/bbAdjust.png'
import tickImg from '../assets/images/bbTick.png'
import crossImg from '../assets/images/bbBoxedCross.png'
import defaultImage from '../assets/images/default-item-image.png'

export default {
  name: 'TableArtefacts',
  //==================================================================
  // components
  //------------
  components: {
    VueGoodTable,
    CounterDialog,
    ConfirmDialog,
    ToolTip,
  },
  //==================================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //==================================================================
  // props
  //-------
  props: {
    headings: {
      type: Array,
      required: true,
    },
    artefacts: {
      type: Array,
      required: true,
    },
    searchQuery: {
      type: String,
      required: false,
      default: '',
    },
  },
  //==================================================================
  // data
  //------
  data() {
    return {
      selectedValue: null,
      selectedColumn: null,
      selectedArtefact: null,
      isAdjustingClearTimer: null,
      //
      artefacts_array: [],
      //
      exclamationImg,
      adjustImg,
      tickImg,
      crossImg,
      defaultImage,
      isAdjusting: false,
      scrollRefreshRaf: null,
      scrollRefreshTimeout: null,
      verticalScrollElement: null,
    }
  },
  //==================================================================
  // computed
  //----------
  computed: {
    tableRenderKey() {
      return this.headings
        .map((heading) => `${heading.field}:${heading.hidden ? 1 : 0}`)
        .join('|')
    },
  },
  //====================================================================
  // methods
  //---------
  methods: {
    getHorizontalScrollState() {
      const content = document.getElementById('artefacts-board')
      if (!content) {
        return { canScrollLeft: false, canScrollRight: false }
      }

      const left = content.scrollLeft
      const maxLeft = Math.max(0, content.scrollWidth - content.clientWidth)
      const epsilon = 10

      return {
        canScrollLeft: left > epsilon,
        canScrollRight: left < maxLeft - epsilon,
      }
    },
    getVerticalScrollTargets() {
      const wrapper = document.getElementById('artefacts-board')
      const responsive = this.$el?.querySelector('.vgt-responsive')

      return [wrapper, responsive].filter(
        (el, idx, arr) => !!el && arr.indexOf(el) === idx,
      )
    },
    getVerticalScrollElement() {
      const targets = this.getVerticalScrollTargets()
      if (targets.length === 0) {
        return null
      }

      let best = targets[0]
      let bestOverflow = Math.max(0, best.scrollHeight - best.clientHeight)

      targets.forEach((el) => {
        const overflow = Math.max(0, el.scrollHeight - el.clientHeight)
        if (overflow > bestOverflow) {
          best = el
          bestOverflow = overflow
        }
      })

      return best
    },
    getVerticalScrollState() {
      const targets = this.getVerticalScrollTargets()
      const epsilon = 0
      const targetState = targets.reduce(
        (acc, content) => {
          const top = content.scrollTop
          const maxTop = Math.max(
            0,
            content.scrollHeight - content.clientHeight,
          )

          return {
            canScrollDown: acc.canScrollDown || top < maxTop - epsilon,
            canScrollUp: acc.canScrollUp || top > epsilon,
          }
        },
        { canScrollDown: false, canScrollUp: false },
      )

      const footerEl =
        this.$el?.querySelector('.vgt-wrap__footer') ||
        this.$el?.querySelector('.vgt-pagination')
      const headerEl =
        this.$el?.querySelector('.vgt-table thead') ||
        this.$el?.querySelector('.vgt-global-search') ||
        this.$el

      const footerRect = footerEl?.getBoundingClientRect()
      const headerRect = headerEl?.getBoundingClientRect()

      const viewportState = {
        canScrollDown:
          !!footerRect && footerRect.bottom > window.innerHeight + 2,
        canScrollUp: !!headerRect && headerRect.top < -2,
      }

      return {
        canScrollDown: targetState.canScrollDown || viewportState.canScrollDown,
        canScrollUp: targetState.canScrollUp || viewportState.canScrollUp,
      }
    },
    bindVerticalScrollListener() {
      const nextElement = this.getVerticalScrollElement()
      if (!nextElement || this.verticalScrollElement === nextElement) {
        return
      }

      if (this.verticalScrollElement) {
        this.verticalScrollElement.removeEventListener(
          'scroll',
          this.scheduleScrollStateRefresh,
        )
      }

      this.verticalScrollElement = nextElement
      this.verticalScrollElement.addEventListener(
        'scroll',
        this.scheduleScrollStateRefresh,
        { passive: true },
      )
    },
    scheduleScrollStateRefresh() {
      if (this.scrollRefreshRaf !== null) {
        return
      }

      this.scrollRefreshRaf = window.requestAnimationFrame(() => {
        this.scrollRefreshRaf = null
        this.$emit('refresh-scroll-state')
      })
    },
    isRowTallyable(row) {
      const value = row?.tallyable

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return ['true', '1', 'yes', 'y', 'on'].includes(normalized)
      }

      if (typeof value === 'number') {
        return value === 1
      }

      return value === true
    },
    //==================================================================
    // is tick visible
    //-----------------
    isTickVisible(props) {
      const field = props.column.field
      if (!field || !field.endsWith('_image') || field === 'adjust_image') {
        return 'bb-img-hidden'
      }

      const baseKey = field.replace(/_image$/, '')
      if (props.row && props.row[baseKey] === true) {
        return 'bb-img-visible'
      }

      return 'bb-img-hidden'
    },
    //==================================================================
    // is exclamation visible
    //------------------------
    isExclamationVisible(props) {
      const field = props.column.field
      if (!field) {
        return 'bb-img-hidden'
      }

      if (props.row && props.row['no_location'] === true) {
        return 'bb-img-visible'
      }

      return 'bb-img-hidden'
    },
    //==================================================================
    // on cell click - open artefact sheet
    //-------------------------------------
    onCellClick(params) {
      console.log('onCellClick params = ', params)
      // --------------------------------------------------------------
      // prevent cell click if deleting or clicking from action column
      // --------------------------------------------------------------
      if (this.isAdjusting || params.column.field.substring(0, 6) == 'action') {
        return
      }
      console.log('onCellClick for artefact = ', params.row)
      // --------------------
      // show artefact panel
      // --------------------
      this.$emit('show-artefact-panel', params.row)
    },
    //==================================================================
    // colour rows
    //-------------
    rowStyleClassFn(row) {
      if (row.originalIndex % 2 === 0) {
        return 'rowNeutral'
      } else {
        return 'rowParty'
      }
    },
    //==================================================================
    // handle scroll table
    //---------------------
    handleScrollTable(dir) {
      if (dir === 'up' || dir === 'down') {
        const targets = this.getVerticalScrollTargets()
        const activeTargets = targets.filter(
          (content) => content.scrollHeight - content.clientHeight > 2,
        )

        const footerEl =
          this.$el?.querySelector('.vgt-wrap__footer') ||
          this.$el?.querySelector('.vgt-pagination')
        const headerEl =
          this.$el?.querySelector('.vgt-table thead') ||
          this.$el?.querySelector('.vgt-global-search') ||
          this.$el

        const finalTops = new Map()

        if (activeTargets.length > 0) {
          activeTargets.forEach((content) => {
            const maxTop = Math.max(
              0,
              content.scrollHeight - content.clientHeight,
            )
            const currentTop = content.scrollTop
            const step = Math.max(Math.floor(content.clientHeight * 0.75), 220)

            let targetTop =
              dir === 'down'
                ? Math.min(maxTop, currentTop + step)
                : Math.max(0, currentTop - step)

            if (dir === 'down' && maxTop - targetTop < 28) {
              targetTop = maxTop
            }

            if (dir === 'up' && targetTop < 10) {
              targetTop = 0
            }

            content.scrollTo({
              top: targetTop,
              behavior: 'smooth',
            })

            finalTops.set(content, targetTop)
          })
        }

        const viewportStep = Math.max(
          Math.floor(window.innerHeight * 0.55),
          180,
        )
        window.scrollBy({
          top: dir === 'down' ? viewportStep : -viewportStep,
          behavior: 'smooth',
        })

        this.scheduleScrollStateRefresh()

        if (this.scrollRefreshTimeout) {
          clearTimeout(this.scrollRefreshTimeout)
          this.scrollRefreshTimeout = null
        }

        this.scrollRefreshTimeout = setTimeout(() => {
          finalTops.forEach((top, content) => {
            content.scrollTo({
              top,
              behavior: 'auto',
            })
          })

          const footerRect = footerEl?.getBoundingClientRect()
          const headerRect = headerEl?.getBoundingClientRect()

          if (
            dir === 'down' &&
            footerEl &&
            footerRect &&
            footerRect.bottom > window.innerHeight &&
            Array.from(finalTops.entries()).every(([content, top]) => {
              const maxTop = Math.max(
                0,
                content.scrollHeight - content.clientHeight,
              )
              return top >= maxTop - 2
            })
          ) {
            footerEl.scrollIntoView({ behavior: 'auto', block: 'end' })
          }

          if (
            dir === 'up' &&
            headerEl &&
            headerRect &&
            headerRect.top < 0 &&
            Array.from(finalTops.values()).every((top) => top <= 2)
          ) {
            headerEl.scrollIntoView({ behavior: 'auto', block: 'start' })
          }

          this.scrollRefreshTimeout = null
          this.$emit('refresh-scroll-state')
        }, 360)
        return
      }

      const content = document.getElementById('artefacts-board')
      if (!content) {
        return
      }

      const maxLeft = Math.max(0, content.scrollWidth - content.clientWidth)
      const step = Math.max(Math.floor(content.clientWidth * 0.75), 240)
      const currentLeft = content.scrollLeft

      let targetLeft =
        dir === 'right'
          ? Math.min(maxLeft, currentLeft + step)
          : Math.max(0, currentLeft - step)

      if (targetLeft < 10) {
        targetLeft = 0
      }

      content.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      })

      this.scheduleScrollStateRefresh()

      if (this.scrollRefreshTimeout) {
        clearTimeout(this.scrollRefreshTimeout)
        this.scrollRefreshTimeout = null
      }

      this.scrollRefreshTimeout = setTimeout(() => {
        this.scrollRefreshTimeout = null
        this.$emit('refresh-scroll-state')
      }, 360)
    },
    //==================================================================
    // adjust quantity
    //-----------------
    async adjustQuantity(row) {
      console.log('adjustQuantity...')

      const name = row['name'] || '' // artefact name e.g. 'Oil Flasks'
      console.log('adjustQuantity row=', row)
      console.log('adjustQuantity for name=', name)
      // ------------------------------------
      // prevent cell click whilst adjusting
      // ------------------------------------
      this.isAdjusting = true
      // --------------------
      // show counter dialog
      // --------------------
      try {
        const newQty = await this.$refs.counterDialog.show({
          title: row['name'],
          label: 'Qty',
          row: row,
          character: this.character,
          okButton: 'OK',
          cancelButton: 'Cancel',
        })
        // ------------------------------------
        // newQty - call emit update artefact quantity
        // ------------------------------------
        if (newQty !== null && newQty !== undefined) {
          console.log('adjustQuantity OK for row=', newQty)
          //  set parms
          const key = 'tally_qty'
          // normalise to numeric so zero-check works even if
          // CounterDialog returns a string like '0'
          const value =
            typeof newQty === 'string' ? parseInt(newQty, 10) : newQty
          const id = row['id']

          const isArtefact = (row.actual_type ?? row.type) === 'Artefact'

          // If this is an artefact and tally is being set to zero,
          // warn that the artefact will be deleted.
          if (value === 0 && isArtefact) {
            const ok = await this.$refs.confirmDialog.show({
              title: 'Delete Artefact Artefact',
              message: 'Setting Qty to zero deletes this artefact.',
              subject: row['name'],
              okButton: 'Delete',
              cancelButton: 'Cancel',
            })

            if (!ok) {
              // user aborted delete; do not proceed
              return
            }
          }

          // If setting tally_qty to zero and the artefact has
          // linked values, confirm via ClearDialog first.
          if (value === 0 && this.hasLinkedValues(row)) {
            const adjustments = this.buildAdjustmentReasonsForArtefact(id)

            const resp = await this.$refs.clearDialog.show({
              title: isArtefact
                ? 'Delete Artefact & Linked Values'
                : 'Clear Linked Values',
              subject: 'Tally quantity being set to zero:',
              message: isArtefact
                ? 'This will delete the artefact artefact and remove the following linked values:'
                : 'The following linked values will be removed from this artefact:',
              artefact: row,
              character: this.character,
              adjustments,
              okButton: isArtefact ? 'Delete' : 'OK',
              cancelButton: 'Cancel',
            })

            if (resp && resp.cancel) {
              // user aborted clear; do not emit update / delete
              return
            }
          }

          // For artefacts with tally set to zero, treat this as a
          // deletion rather than a simple tally update so that the
          // normal handleDeleteArtefact / processDeleteArtefact flow runs.
          if (value === 0 && isArtefact) {
            this.$emit('delete-campaign-artefact', row)
            return
          }

          // either non-zero tally, no linked values,
          // or user confirmed the clear operation
          this.emitUpdate(key, value, name, id)
        }
      } finally {
        // -------------------------
        // clear any existing timer
        // -------------------------
        if (this.isAdjustingClearTimer) clearTimeout(this.isAdjustingClearTimer)
        // ----------------------------------
        // delay clearing isAdjusting (300ms)
        // ----------------------------------
        this.isAdjustingClearTimer = setTimeout(() => {
          this.isAdjusting = false
          this.isAdjustingClearTimer = null
        }, 300)
      }
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value, name, id) {
      // e.g. type = 'artefacts', key = "tally_qty", value = 2, name = 'Oil Flasks', id = 123
      console.log(
        'emitUpdate key = ',
        key,
        ' value = ',
        value,
        ' name = ',
        name,
        ' id = ',
        id,
      )
      this.$emit('update-artefact-qty', {
        type: 'artefacts',
        key,
        value,
        name,
        id,
      })
    },
  },
  mounted() {
    this.bindVerticalScrollListener()
    window.addEventListener('scroll', this.scheduleScrollStateRefresh, {
      passive: true,
    })
    this.scheduleScrollStateRefresh()
  },
  updated() {
    this.bindVerticalScrollListener()
    this.scheduleScrollStateRefresh()
  },
  //==================================================================
  // before unmount
  //----------------
  beforeUnmount() {
    // ------------------------
    // clear any pending timer
    // ------------------------
    if (this.isAdjustingClearTimer) {
      clearTimeout(this.isAdjustingClearTimer)
      this.isAdjustingClearTimer = null
    }

    if (this.scrollRefreshRaf !== null) {
      window.cancelAnimationFrame(this.scrollRefreshRaf)
      this.scrollRefreshRaf = null
    }

    if (this.scrollRefreshTimeout) {
      clearTimeout(this.scrollRefreshTimeout)
      this.scrollRefreshTimeout = null
    }

    if (this.verticalScrollElement) {
      this.verticalScrollElement.removeEventListener(
        'scroll',
        this.scheduleScrollStateRefresh,
      )
      this.verticalScrollElement = null
    }

    window.removeEventListener('scroll', this.scheduleScrollStateRefresh)
  },
}
</script>

<style scoped>
:deep(.vgt-table thead th) {
  padding-top: 0px !important;
  padding-bottom: 16px !important;
  margin-top: 0px !important;
  margin-bottom: 0px !important;
}
</style>
